"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";
import { DropdownAuroraGlow } from "./composites/dropdown-aurora-glow";
import { LiquidPill } from "./liquid-pill";

const sideModalPanelVariants = cva(
  [
    "relative flex h-full w-full flex-col overflow-hidden",
    "border border-slate-200/70 bg-white/95 backdrop-blur-xl",
    "shadow-[0_32px_100px_-18px_rgba(15,23,42,0.35),0_18px_48px_-18px_rgba(79,70,229,0.18),inset_0_1px_0_rgba(255,255,255,0.72)]",
    "dark:border-zinc-800/80 dark:bg-zinc-950/95 dark:shadow-[0_32px_100px_-18px_rgba(0,0,0,0.58),0_18px_48px_-18px_rgba(99,102,241,0.2),inset_0_1px_0_rgba(255,255,255,0.05)]",
  ].join(" "),
  {
    variants: {
      side: {
        right: "rounded-l-[24px] border-r-0",
        left: "rounded-r-[24px] border-l-0",
      },
      size: {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
        wide: "max-w-[min(92vw,960px)]",
      },
    },
    defaultVariants: {
      side: "right",
      size: "lg",
    },
  }
);

export interface SideModalProps extends VariantProps<typeof sideModalPanelVariants> {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  eyebrow?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  className?: string;
  panelClassName?: string;
  panelStyle?: CSSProperties;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  backdropClassName?: string;
  backdrop?: boolean | "static";
  keyboard?: boolean;
  showCloseButton?: boolean;
  closeButtonLabel?: string;
}

export function SideModal({
  open,
  onClose,
  title,
  description,
  eyebrow,
  children,
  footer,
  header,
  side = "right",
  size = "lg",
  className,
  panelClassName,
  panelStyle,
  headerClassName,
  bodyClassName,
  footerClassName,
  backdropClassName,
  backdrop = true,
  keyboard = true,
  showCloseButton = true,
  closeButtonLabel = "Close side modal",
}: SideModalProps) {
  const [mounted, setMounted] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(open);
  const [isVisible, setIsVisible] = React.useState(open);
  const titleId = React.useId();
  const descriptionId = React.useId();

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  React.useEffect(() => {
    if (!mounted) return;

    if (open) {
      setShouldRender(true);
      setIsVisible(false);
      const openId = window.setTimeout(() => setIsVisible(true), 28);
      return () => window.clearTimeout(openId);
    }

    setIsVisible(false);
    const closeId = window.setTimeout(() => setShouldRender(false), 420);
    return () => window.clearTimeout(closeId);
  }, [mounted, open]);

  React.useEffect(() => {
    if (!mounted || !shouldRender) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted, shouldRender]);

  React.useEffect(() => {
    if (!open || !keyboard) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [keyboard, onClose, open]);

  const handleBackdropClick = React.useCallback(() => {
    if (backdrop === true) {
      onClose();
    }
  }, [backdrop, onClose]);

  const hasHeaderContent = Boolean(header || eyebrow || title || description);
  const showHeader = hasHeaderContent;
  const slideOffset = side === "right" ? "100%" : "-100%";

  if (!mounted || !shouldRender) {
    return null;
  }

  return createPortal(
    <div
      className={cn("fixed inset-0", className)}
      style={{ zIndex: Z_INDEX.MODAL }}
      aria-hidden={!isVisible}
    >
      {backdrop !== false && (
        <button
          type="button"
          aria-label="Close side modal backdrop"
          className={cn(
            "absolute inset-0 cursor-default bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.15),transparent_32%)] bg-black/45 backdrop-blur-[6px]",
            backdropClassName
          )}
          style={{
            opacity: isVisible ? 1 : 0,
            transition: "opacity 360ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          onClick={handleBackdropClick}
        />
      )}

      <div
        className={cn(
          "absolute inset-y-0 flex max-h-full p-2 sm:p-3",
          side === "right" ? "right-0 justify-end" : "left-0 justify-start"
        )}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={!title ? "Side modal" : undefined}
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descriptionId : undefined}
          className={cn(
            sideModalPanelVariants({ side, size }),
            "max-h-full will-change-transform",
            panelClassName
          )}
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible
              ? "translateX(0)"
              : `translateX(${slideOffset})`,
            filter: isVisible ? "blur(0px) saturate(1)" : "blur(6px) saturate(1.06)",
            transition:
              "opacity 420ms cubic-bezier(0.16, 1, 0.3, 1), transform 460ms cubic-bezier(0.16, 1, 0.3, 1), filter 420ms cubic-bezier(0.16, 1, 0.3, 1)",
            ...panelStyle,
          }}
        >
          <div className="pointer-events-none absolute inset-0 rounded-[inherit] border border-white/50 dark:border-white/5" />
          <DropdownAuroraGlow variant="modal" />

          <div className="relative z-10 flex min-h-0 flex-1 flex-col">
            {showHeader && (
              <div
                className={cn(
                  "relative flex shrink-0 items-start justify-between gap-3 bg-transparent px-4 pb-3 pt-4 sm:px-5 sm:pb-4 sm:pt-5",
                  headerClassName
                )}
              >
                <div className="min-w-0 flex-1">
                  {header ?? (
                    <>
                      {eyebrow && (
                        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-indigo-500/90 dark:text-indigo-300/80">
                          {eyebrow}
                        </p>
                      )}
                      {title && (
                        <h2
                          id={titleId}
                          className="mt-0.5 text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]"
                        >
                          {title}
                        </h2>
                      )}
                      {description && (
                        <p
                          id={descriptionId}
                          className="mt-1 max-w-2xl text-[11px] text-slate-500 dark:text-zinc-400 sm:text-xs"
                        >
                          {description}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {showCloseButton && hasHeaderContent && (
                  <LiquidPill
                    as="button"
                    type="button"
                    onClick={onClose}
                    aria-label={closeButtonLabel}
                    variant="rose"
                    appearance="tinted"
                    shape="circle"
                    size="sm"
                    icon={<X className="h-3.5 w-3.5" />}
                  />
                )}
              </div>
            )}

            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto bg-transparent px-4 sm:px-5",
                showHeader ? "pb-4 pt-1 sm:pb-5 sm:pt-1.5" : "py-4 sm:py-5",
                bodyClassName
              )}
            >
              {children}
            </div>

            {footer && (
              <div
                className={cn(
                  "shrink-0 border-t border-slate-200/70 bg-transparent px-4 py-3 dark:border-zinc-800/90 sm:px-5",
                  footerClassName
                )}
              >
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
